import { debug } from '../../../stores/store';
import { AccountRequestSchema, type AccountResponseType } from '../Settings/SettingsApi';
import { defaultAccount } from './defaultAccount';
import { get } from 'svelte/store';
import type { PermissionsType } from '../../../stores/app';

export type AccountType = 'standard' | 'trial';

export class Account {
    private readonly _innerId: string;
    private _id: number | null = null;
    private _accountData: AccountResponseType | null = null;

    constructor() {
        this._innerId = Math.random().toString(36);
    }

    public get innerId(): string {
        return this._innerId;
    }

    public get() {
        return this._accountData;
    }

    public set(data: AccountResponseType, id: number | null): this {
        this._id = id;
        this._accountData = JSON.parse(JSON.stringify(data));
        return this;
    }

    public getId() {
        return this._id;
    }

    public isNew() {
        return this._id === null;
    }

    public getName() {
        return this._accountData?.name || '';
    }

    public setName(name: string) {
        this._accountData = {
            ...(this._accountData ?? defaultAccount),
            name,
        };
        return this;
    }

    public getEmail() {
        return this._accountData?.email || '';
    }

    public setEmail(email: string) {
        this._accountData = {
            ...(this._accountData ?? defaultAccount),
            email,
        };
        return this;
    }

    public getExpirationDate() {
        return this._accountData?.expiration_date || '';
    }

    public setExpirationDate(expiration_date: string) {
        this._accountData = {
            ...(this._accountData ?? defaultAccount),
            expiration_date,
        };
        return this;
    }

    public getCustomerId(): number | null {
        return this._accountData?.customer_id || null;
    }

    public getType(): AccountType {
        if (this.getExpirationDate() === '') {
            return 'standard';
        }
        return 'trial';
    }

    public isExpired(): boolean | null {
        if (this._accountData?.is_expired !== null) {
            return this._accountData?.is_expired || false;
        } else {
            if (this.getExpirationDate() === '') {
                return false;
            }
            const expirationDate = new Date(this.getExpirationDate());
            expirationDate.setHours(23, 59, 59, 999);
            const today = new Date();
            return expirationDate < today;
        }
    }

    public getWebuserId(): number | null {
        return this._accountData?.webuser_id ?? null;
    }

    /**
     * Uprawnienia konta widziane przez panel admina (US-018). Zwraca null, dopóki backend ich nie
     * przysłał - przełącznik roli user_manager nie ma się wtedy z czego wyliczyć i nie jest
     * pokazywany, zamiast domyślać się stanu.
     */
    public getPermissions(): PermissionsType | null {
        const data = this._accountData;
        if (
            typeof data?.can_read !== 'boolean' ||
            typeof data?.can_write !== 'boolean' ||
            typeof data?.is_user_manager !== 'boolean'
        ) {
            return null;
        }

        return {
            canRead: data.can_read,
            canWrite: data.can_write,
            isUserManager: data.is_user_manager,
        };
    }

    /** Stan po odpowiedzi Admin::setUserManager() - zapisujemy to, co wróciło z backendu. */
    public setPermissions(permissions: PermissionsType) {
        this._accountData = {
            ...(this._accountData ?? defaultAccount),
            can_read: permissions.canRead,
            can_write: permissions.canWrite,
            is_user_manager: permissions.isUserManager,
        };
        return this;
    }

    public getSettingsReadonly() {
        return this._accountData?.settings_readonly || null;
    }

    public setSettingsReadonly(settings_readonly: any) {
        this._accountData = {
            ...(this._accountData ?? defaultAccount),
            settings_readonly,
        };
        return this;
    }
}
